// src/api/transactionsApi.ts
import { baseApi } from "./api";
import {
  Transaction,
  TransactionPayload,
  TransactionListResponse,
  TransactionFilters,
  MonthlyEarnings,
  EarningsSummary,
  EarningsByCategoryResponse,
  OrdersSummary,
} from "@nicsmith/shared_types";

export const transactionsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Fetch all transactions (admin)
    fetchTransactions: builder.query<
      TransactionListResponse,
      { page?: number; limit?: number; filters?: TransactionFilters }
    >({
      query: ({ page = 1, limit = 20, filters = {} }) => {
        const params = new URLSearchParams();
        params.append("page", page.toString());
        params.append("limit", limit.toString());
        if (filters.status) params.append("status", filters.status);
        if (filters.type) params.append("type", filters.type);
        if (filters.user_uid) params.append("user_uid", filters.user_uid);
        if (filters.from_date) params.append("from_date", filters.from_date);
        if (filters.to_date) params.append("to_date", filters.to_date);
        return `/transactions?${params.toString()}`;
      },
      providesTags: ["Transaction"],
    }),

    // Fetch recent transactions (for dashboard widget)
    fetchRecentTransactions: builder.query<Transaction[], number | void>({
      query: (limit = 10) => `/transactions/recent?limit=${limit}`,
      providesTags: ["Transaction"],
    }),

    // Fetch single transaction
    fetchTransaction: builder.query<Transaction, number>({
      query: (id) => `/transactions/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Transaction", id }],
    }),

    // Fetch user's transactions
    fetchUserTransactions: builder.query<
      { transactions: Transaction[]; has_more: boolean },
      { userUid: string; limit?: number }
    >({
      query: ({ userUid, limit = 20 }) =>
        `/transactions/user/${userUid}?limit=${limit}`,
      providesTags: ["Transaction"],
    }),

    // Create transaction
    createTransaction: builder.mutation<Transaction, TransactionPayload>({
      query: (payload) => ({
        url: "/transactions",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Transaction", "Analytics"],
    }),

    // Update transaction status
    updateTransactionStatus: builder.mutation<
      Transaction,
      { id: number; status: string }
    >({
      query: ({ id, status }) => ({
        url: `/transactions/${id}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Transaction", id },
        "Transaction",
        "Analytics",
      ],
    }),

    // Delete transaction
    deleteTransaction: builder.mutation<void, number>({
      query: (id) => ({
        url: `/transactions/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Transaction", "Analytics"],
    }),

    // Analytics: Earnings summary
    fetchEarningsSummary: builder.query<EarningsSummary, number | void>({
      query: (year) =>
        `/analytics/earnings${year ? `?year=${year}` : ""}`,
      providesTags: ["Analytics"],
    }),

    // Analytics: Monthly earnings
    fetchMonthlyEarnings: builder.query<MonthlyEarnings[], number | void>({
      query: (year) =>
        `/analytics/earnings/monthly${year ? `?year=${year}` : ""}`,
      providesTags: ["Analytics"],
    }),

    // Analytics: Current month earnings
    fetchCurrentMonthEarnings: builder.query<
      {
        total_earnings: number;
        period_label: string;
        previous_period_earnings: number;
        change_percentage: number;
      },
      void
    >({
      query: () => "/analytics/earnings/current-month",
      providesTags: ["Analytics"],
    }),

    // Analytics: Earnings by category
    fetchEarningsByCategory: builder.query<EarningsByCategoryResponse, void>({
      query: () => "/analytics/earnings/by-category",
      providesTags: ["Analytics"],
    }),

    // Analytics: Orders summary
    fetchOrdersSummary: builder.query<OrdersSummary, void>({
      query: () => "/analytics/orders/summary",
      providesTags: ["Analytics"],
    }),

    // Analytics: Visitors (PostHog-backed) for admin dashboard card
    fetchVisitors: builder.query<
      {
        today: number | null;
        thisWeek: number | null;
        thisMonth: number | null;
        dailySeries: Array<{ day: string; visitors: number }>;
        generatedAt: string;
        error?: string;
      },
      void
    >({
      query: () => "/analytics/visitors",
      providesTags: ["Analytics"],
    }),
  }),
  overrideExisting: true,
});

// Export hooks for usage in components
export const {
  useFetchTransactionsQuery,
  useFetchRecentTransactionsQuery,
  useFetchTransactionQuery,
  useFetchUserTransactionsQuery,
  useCreateTransactionMutation,
  useUpdateTransactionStatusMutation,
  useDeleteTransactionMutation,
  useFetchEarningsSummaryQuery,
  useFetchMonthlyEarningsQuery,
  useFetchCurrentMonthEarningsQuery,
  useFetchEarningsByCategoryQuery,
  useFetchOrdersSummaryQuery,
  useFetchVisitorsQuery,
} = transactionsApi;
